import React from 'react';
import { 
  FiX, 
  FiUser, 
  FiArrowUp, 
  FiArrowDown,
  FiLink,
  FiUsers
} from 'react-icons/fi';

/**
 * ReferralChainView Component
 * Modal to display a user's referral chain (upline and downline)
 */
const ReferralChainView = ({ 
  isOpen, 
  onClose, 
  chainData, 
  loading,
  selectedUser
}) => {
  if (!isOpen) return null;

  const referredBy = chainData?.chain?.filter(m => m.type === 'referred_by') || [];
  const referred = chainData?.chain?.filter(m => m.type === 'referred') || [];
  // Hierarchy tree (2026-06-05): nest second-level referrals under the direct
  // (L1 onboarding) referral who brought them in.
  const directOnboarding = referred.filter(m => m.level === 1 && m.source === 'onboarding');
  const directEvent = referred.filter(m => m.level === 1 && m.source !== 'onboarding');
  const secondLevel = referred.filter(m => m.level === 2);
  const childrenOf = (uid) => secondLevel.filter(m => String(m.parent_id) === String(uid));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-indigo-500 to-purple-600">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-full">
              <FiLink className="text-white text-xl" />
            </div>
            <div className="text-white">
              <h3 className="text-lg font-semibold">Referral Chain</h3>
              <p className="text-sm text-white/80">@{selectedUser?.username || chainData?.username}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <FiX className="text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {loading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-24 bg-gray-200 rounded-lg"></div>
              <div className="h-16 bg-gray-200 rounded-lg"></div>
              <div className="h-24 bg-gray-200 rounded-lg"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Upline Section (Referred By) */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <FiArrowUp className="text-blue-500" />
                  <h4 className="font-semibold text-gray-700">Upline (Referred By)</h4>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    {referredBy.length} user{referredBy.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {referredBy.length === 0 ? (
                  <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500">
                    <FiUser className="mx-auto text-2xl mb-2 text-gray-300" />
                    <p>No upline referrers</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {referredBy.map((member, index) => (
                      <div
                        key={member.user_id || index}
                        className="flex items-center gap-4 p-3 bg-blue-50 rounded-lg border border-blue-100"
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                          member.level === 1 ? 'bg-blue-500' : 'bg-blue-400'
                        }`}>
                          L{member.level}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-800">@{member.username}</p>
                          <p className="text-xs text-gray-500 truncate">{member.user_id}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${
                          member.level === 1 
                            ? 'bg-blue-200 text-blue-800' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {member.level === 1 ? 'Direct Referrer' : 'Indirect Referrer'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Current User */}
              <div className="relative">
                <div className="absolute left-1/2 -top-3 transform -translate-x-1/2">
                  <div className="w-6 h-6 bg-gray-200 rounded-full border-2 border-white"></div>
                </div>
                <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-4 text-white text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-2">
                    <FiUser className="text-3xl" />
                  </div>
                  <p className="font-bold text-lg">@{chainData?.username || selectedUser?.username}</p>
                  <p className="text-sm text-white/80">{chainData?.user_id || selectedUser?.userId}</p>
                </div>
                <div className="absolute left-1/2 -bottom-3 transform -translate-x-1/2">
                  <div className="w-6 h-6 bg-gray-200 rounded-full border-2 border-white"></div>
                </div>
              </div>

              {/* Downline Section (Referred) */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <FiArrowDown className="text-green-500" />
                  <h4 className="font-semibold text-gray-700">Downline (Referred Users)</h4>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                    {referred.length} user{referred.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {referred.length === 0 ? (
                  <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500">
                    <FiUsers className="mx-auto text-2xl mb-2 text-gray-300" />
                    <p>No referred users yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Onboarding referrals as a nested tree (L1 → their L2) */}
                    {directOnboarding.map((m, index) => {
                      const kids = childrenOf(m.user_id);
                      return (
                        <div key={m.user_id || index} className="rounded-lg border border-green-100 overflow-hidden">
                          <div className="flex items-center gap-3 p-3 bg-green-50">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold bg-green-500">L1</div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-800 text-sm">@{m.username || m.user_id}</p>
                              <p className="text-xs text-gray-500 truncate">{m.user_id}</p>
                            </div>
                            <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full">onboarding</span>
                          </div>
                          {kids.length > 0 && (
                            <div className="pl-6 pr-3 py-2 bg-white border-t border-green-100 space-y-2">
                              {kids.map((c, ci) => (
                                <div key={c.user_id || ci} className="flex items-center gap-3">
                                  <div className="text-green-300 font-mono text-xs">└─</div>
                                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold bg-green-400">L2</div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-700 text-sm">@{c.username || c.user_id}</p>
                                    <p className="text-[10px] text-gray-400 truncate">{c.user_id}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Event-participation referrals (used this user's code at an event) */}
                    {directEvent.length > 0 && (
                      <div className="rounded-lg border border-amber-100 overflow-hidden">
                        <div className="px-3 py-2 bg-amber-50 text-xs font-semibold text-amber-700">
                          Event referrals ({directEvent.length})
                        </div>
                        <div className="p-2 space-y-1 bg-white">
                          {directEvent.map((m, i) => (
                            <div key={(m.user_id || i) + '-ev'} className="flex items-center gap-3 px-2 py-1">
                              <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold bg-amber-500">L1</div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-700 text-sm">@{m.username || m.user_id}</p>
                                <p className="text-[10px] text-gray-400 truncate">{m.user_id}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Stats Summary */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-blue-600">{referredBy.length}</p>
                    <p className="text-xs text-gray-600">Upline</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-purple-600">{chainData?.chain?.length || 0}</p>
                    <p className="text-xs text-gray-600">Total Chain</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-green-600">{referred.length}</p>
                    <p className="text-xs text-gray-600">Downline</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferralChainView;
